import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { whenOptions } from "../../config/constants/options";
import { AppDispatch } from "../store";
import {
  getConsultationDocuments,
  getDocuments,
} from "../../services/media.services";
import { Complaint, ConsultationState, Diagnosis, Prescription } from "../../types/consultation";

const initialState: ConsultationState = {
  formData: {
    weight: "",
    height: "",
    pulse: "",
    temperature: "",
    bp: "",
    complaint: "",
    frequency: "",
    severity: "",
    duration: "",
    date: null,
    clinicalHistory: "",
    additionalNotes: "",
    advice: "",
    test: [],
    unit: "",
    consultFlag: null,
    uuid: "",
    customerId: "",
    markDone: false,
    testInputValue: "",
  },
  errors: {},
  selectOptions: {
    complaint: [],
    frequency: [],
    severity: [],
    medicine: [],
    dose: [],
    when: whenOptions,
    test: [],
    diagnosis: [],
  },
  tables: {
    diagnosis: [{ no: 0, diagnosis: "", duration: "", date: null, unit: "" }],
    prescription: [
      {
        no: 0,
        medicine: "",
        dose: "",
        when: "",
        frequency: "",
        duration: "",
        notes: "",
        unit: "",
      },
    ],
    complaints: [
      {
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        unit: "",
        date: null,
      } as Complaint,
    ],
  },
  pastConsultationData: {},
  mainPaymentHistory: [],
  mediaData: {},
  mediaTabData: {},
};

const consultationSlice = createSlice({
  name: "consultation",
  initialState,
  reducers: {
    setFormData: (
      state,
      action: PayloadAction<Partial<ConsultationState["formData"]>>
    ) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setSelectOptions: (
      state,
      action: PayloadAction<Partial<ConsultationState["selectOptions"]>>
    ) => {
      state.selectOptions = { ...state.selectOptions, ...action.payload };
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    setPastConsultationData: (state, action: PayloadAction<any>) => {
      state.pastConsultationData = action.payload;
    },
    setMediaData: (state, action: PayloadAction<any>) => {
      state.mediaData = action.payload;
    },
    setMediaTabData: (state, action: PayloadAction<any>) => {
      state.mediaTabData = action.payload;
    },
    setPaymentHistoryData: (state, action: PayloadAction<any>) => {
      state.mainPaymentHistory = action.payload;
    },
    setTable: (
      state,
      action: PayloadAction<{
        table: "diagnosis" | "prescription" | "complaints";
        rows: Diagnosis[] | Prescription[] | Complaint[];
      }>
    ) => {
      const { table, rows } = action.payload;
      if (table === "diagnosis") {
        state.tables.diagnosis = rows as Diagnosis[];
      } else if (table === "prescription") {
        state.tables.prescription = rows as Prescription[];
      } else if (table === "complaints") {
        state.tables.complaints = rows as Complaint[];
      }
    },
    clearErrors: (state) => {
      state.errors = {};
    },
    clearAPIData: (state) => {
      state.pastConsultationData = {};
      state.mainPaymentHistory = [];
      state.mediaData = {};
    },
    clearForm: (state) => {
      state.formData = {
        ...initialState.formData,
        customerId: state.formData.customerId,
      };
      state.errors = {};
      state.pastConsultationData = {};
      state.mainPaymentHistory = [];
      state.mediaData = {};
      state.tables = {
        diagnosis: [
          { no: 0, diagnosis: "", duration: "", date: null, unit: "" },
        ],
        prescription: [
          {
            no: 0,
            medicine: "",
            dose: "",
            when: "",
            frequency: "",
            duration: "",
            notes: "",
            unit: "",
          },
        ],
        complaints: [
          {
            complaint: "",
            frequency: "",
            severity: "",
            duration: "",
            unit: "",
            date: null,
          } as Complaint,
        ],
      };
    },
  },
});

export const {
  setFormData,
  setErrors,
  clearErrors,
  clearForm,
  setSelectOptions,
  setTable,
  setPastConsultationData,
  setPaymentHistoryData,
  setMediaData,
  setMediaTabData,
  clearAPIData,
} = consultationSlice.actions;

// Thunk to handle fetching media data by ID
export const getMediaDataById =
  (caseId: string): any =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await getConsultationDocuments(caseId);
      if (res?.data?.status === 200) {
        dispatch(setMediaData(res?.data?.data));
      }
    } catch (err) {
      dispatch(setMediaData({}));
    }
  };

export const getMediaTabDataById =
  (caseId: string): any =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await getDocuments(caseId);
      if (res?.data?.status === 200) {
        dispatch(setMediaTabData(res?.data?.data));
      }
    } catch (err) {
      dispatch(setMediaTabData({}));
    }
  };

export default consultationSlice.reducer;
